import _ from 'lodash';
import {DateTime} from 'luxon';
import React, {FC, useState} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../../libs/shared/core/src/helpers/internationalization/react';
import {NumberInputBox} from '../../../../../libs/shared/ui/src/components/boxedInputs/numberInputBox';
import {OkCancelDialog} from '../../../../../libs/shared/ui/src/components/dialogs/okCancelDialog';
import {LuxonWeekdayToCalendarWeekDays} from '../../../../helpers/models/calendarEventHelpers';
import {baseRecurrenceRule, buildRecurrence} from '../../../../helpers/models/recurringEventHelpers';
import {CalendarWeekDaysEnum} from '../../../../models/api/calendarCommonTypes';
import {
  RecurrenceFrequenciesEnum,
  RecurrenceModel,
  RecurrenceRuleModel,
  RecurrenceWeekdayModel,
} from '../../../../models/api/recurrenceModel';
import {RecurrenceEditorRow} from './recurrenceEditorRow';
import {RecurrenceEndEditor} from './recurrenceEndEditor';
import {RecurrenceFrequencyDropdownBox} from './recurrenceFrequencyDropdownBox';
import {RecurrenceMonthlyWeekdayEditor} from './recurrenceMonthlyWeekdayEditor';
import {RecurrenceWeeklyWeekdayEditor} from './recurrenceWeeklyWeekdayEditor';

/*
 * Props.
 */

export interface CustomRecurrenceDialogProps {
  startDateTime: DateTime;
  weekStart: CalendarWeekDaysEnum;
  selectedRecurrence?: RecurrenceModel;
  onSaveClick: (model: RecurrenceModel) => void;
  onCancelClick: () => void;
}

/*
 * Styles.
 */

const StyledFrequencyIntervalWrapperDiv = styled.div`
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: 50px 100px;
  grid-column-gap: 8px;
`;

const StyledRecurrenceEditorWrapperDiv = styled.div`
  display: grid;
  grid-row-gap: 25px;
`;

/*
 * Component.
 */

const minInterval = 1;
const maxInterval = 360;

export const CustomRecurrenceDialog: FC<CustomRecurrenceDialogProps> = (props) => {
  const {startDateTime, weekStart, onCancelClick} = props;
  const startWeekday = LuxonWeekdayToCalendarWeekDays[startDateTime.weekday];

  const initialRecurrenceRule = getInitialRecurrenceRule(startWeekday, props.selectedRecurrence);

  // byweekday
  const [byweekday, setByweekday] = useState<ReadonlyArray<RecurrenceWeekdayModel> | undefined>(
    initialRecurrenceRule.byweekday,
  );

  // frequency
  const [frequency, setFrequency] = useState<RecurrenceFrequenciesEnum>(initialRecurrenceRule.freq);

  const onFrequencyChange = (newFrequency: RecurrenceFrequenciesEnum) => {
    setFrequency(newFrequency);
    if (newFrequency === RecurrenceFrequenciesEnum.WEEKLY) {
      // when changing to a weekly frequency, default to setting byweekday
      // to the weekday of the currently selected startDateTime
      setByweekday([{weekday: startWeekday, n: undefined}]);
    } else {
      setByweekday(undefined);
    }
  };

  // interval
  const [interval, setInterval] = useState<number>(initialRecurrenceRule.interval || minInterval);

  // until
  const [until, setUntil] = useState<number | undefined>(initialRecurrenceRule.until);

  // count
  const [count, setCount] = useState<number | undefined>(initialRecurrenceRule.count);

  const onSaveClick = () =>
    props.onSaveClick(
      buildRecurrence({
        byweekday,
        freq: frequency,
        interval,
        until,
        count,
        wkst: frequency === RecurrenceFrequenciesEnum.WEEKLY ? props.weekStart : undefined,
      }),
    );

  return (
    <OkCancelDialog
      title={<Trans id="custom-recurrence-title">Repeat event</Trans>}
      okButtonLabel={<Trans id="custom-recurrence-done">Done</Trans>}
      cancelButtonLabel={<Trans id="custom-recurrence-cancel">Cancel</Trans>}
      onOkClick={onSaveClick}
      onRequestClose={onCancelClick}
    >
      <StyledRecurrenceEditorWrapperDiv>
        <RecurrenceEditorRow
          label={<Trans id="custom-recurrence-frequency-dropdown">Repeats every</Trans>}
          content={renderFrequencyIntervalSection(
            startDateTime,
            frequency,
            interval,
            byweekday,
            onFrequencyChange,
            setInterval,
            setByweekday,
          )}
        />
        {frequency === RecurrenceFrequenciesEnum.WEEKLY && (
          <RecurrenceEditorRow
            label={<Trans id="custom-recurrence-weekdays">Repeats on</Trans>}
            content={
              <RecurrenceWeeklyWeekdayEditor
                weekStart={weekStart}
                recurrenceWeekdays={byweekday}
                setRecurrenceWeekdays={setByweekday}
              />
            }
          />
        )}
        <RecurrenceEditorRow
          label={<Trans id="custom-recurrence-ends">Ends</Trans>}
          content={
            <RecurrenceEndEditor
              initialUntil={until}
              initialCount={count}
              onUntilChange={setUntil}
              onCountChange={setCount}
            />
          }
        />
      </StyledRecurrenceEditorWrapperDiv>
    </OkCancelDialog>
  );
};

function getInitialRecurrenceRule(startWeekday: CalendarWeekDaysEnum, selectedRecurrence?: RecurrenceModel) {
  const selectedRecurrenceRule = selectedRecurrence && selectedRecurrence.rrules[0];

  if (selectedRecurrenceRule && isFrontRecurrenceRule(selectedRecurrenceRule)) {
    return selectedRecurrenceRule;
  }

  // Default to a weekly recurrence rule on the weekday of this event.
  return {
    ...baseRecurrenceRule,
    freq: RecurrenceFrequenciesEnum.WEEKLY,
    byweekday: [{weekday: startWeekday, n: undefined}],
  };
}

function isFrontRecurrenceRule(recurrenceRule: RecurrenceRuleModel) {
  // Returns true if all of the properties of recurrenceRule
  // are ones that can be set from Front.
  const nonFrontRecurrenceProperties = _.omit(
    recurrenceRule,
    'freq',
    'interval',
    'byweekday',
    'count',
    'until',
    'wkst',
  );
  return Object.values(nonFrontRecurrenceProperties).every((val) => val === undefined);
}

function renderFrequencyIntervalSection(
  startDateTime: DateTime,
  frequency: RecurrenceFrequenciesEnum,
  interval: number,
  byweekday: ReadonlyArray<RecurrenceWeekdayModel> | undefined,
  setFrequency: (arg: RecurrenceFrequenciesEnum) => void,
  setInterval: (arg: number) => void,
  setByweekday: (arg: Array<RecurrenceWeekdayModel> | undefined) => void,
) {
  return (
    <>
      <StyledFrequencyIntervalWrapperDiv>
        <NumberInputBox
          noMargin
          value={interval}
          onChange={(value) => setInterval(value || 0)}
          min={minInterval}
          max={maxInterval}
        />
        <RecurrenceFrequencyDropdownBox freq={frequency} interval={interval} onChange={setFrequency} />
      </StyledFrequencyIntervalWrapperDiv>
      {frequency === RecurrenceFrequenciesEnum.MONTHLY && (
        <RecurrenceMonthlyWeekdayEditor
          startDateTime={startDateTime}
          recurrenceWeekdays={byweekday}
          setRecurrenceWeekdays={setByweekday}
        />
      )}
    </>
  );
}
